import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { api } from '../lib/api.js';
import { useWorkspaceStore } from '../stores/workspace.js';
import type { SavedView, ViewEntityType } from '@mayksel/types';
import type { CreateViewInput, UpdateViewInput } from '@mayksel/validators';

function useApiOpts() {
  const { getToken } = useAuth();
  const workspaceId = useWorkspaceStore((s) => s.workspace?.id);
  return { getToken, workspaceId };
}

export function useViews(entityType?: ViewEntityType) {
  const { getToken, workspaceId } = useApiOpts();

  return useQuery<SavedView[]>({
    queryKey: ['views', workspaceId, entityType],
    queryFn: async () => {
      const token = await getToken();
      const qs = entityType ? `?entityType=${entityType}` : '';
      return api.get<SavedView[]>(`/v1/views${qs}`, {
        token: token ?? undefined,
        workspaceId: workspaceId ?? undefined,
      });
    },
    enabled: !!workspaceId,
  });
}

export function useCreateView() {
  const { getToken, workspaceId } = useApiOpts();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateViewInput) => {
      const token = await getToken();
      return api.post<SavedView>('/v1/views', data, {
        token: token ?? undefined,
        workspaceId: workspaceId ?? undefined,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['views', workspaceId] });
    },
  });
}

export function useUpdateView(id: string) {
  const { getToken, workspaceId } = useApiOpts();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateViewInput) => {
      const token = await getToken();
      return api.patch<SavedView>(`/v1/views/${id}`, data, {
        token: token ?? undefined,
        workspaceId: workspaceId ?? undefined,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['views', workspaceId] });
    },
  });
}

export function useDeleteView() {
  const { getToken, workspaceId } = useApiOpts();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const token = await getToken();
      return api.delete<{ id: string }>(`/v1/views/${id}`, {
        token: token ?? undefined,
        workspaceId: workspaceId ?? undefined,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['views', workspaceId] });
    },
  });
}
